import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  ChevronDown as ChevronDownIcon,
  Search,
  X,
  Menu,
  Plus,
  Minus,
  Phone,
} from "lucide-react";
import Text from "../Text";
import Img from "../Img";
import { ROUTE_PATHS } from "../../../../constants/routeConstants";
import ProductsMegaMenu from "./organisms/ProductsMegaMenu";
import CategoriesMegaMenu from "./organisms/CategoriesMegaMenu";
import NavDropdownWithImage from "./organisms/NavDropdownWithImage";
import SearchModal from "./organisms/SearchModal";
import { CompanyLogo } from "../../../../Components/Utils";

const NAV_LINK_PATHS = {
  Blog: ROUTE_PATHS.BLOG_DETAIL,
  Brands: ROUTE_PATHS.BRANDS,
};

const NAV_LINKS = [
  {
    label: "Home",
    href: ROUTE_PATHS.HOME,
    desktopOnly: false,
    mobileOnly: false,
  },
  { label: "Categories", href: null, desktopOnly: false, mobileOnly: false },
  { label: "Products", href: null, desktopOnly: false, mobileOnly: false },
  { label: "Blog", href: null, desktopOnly: false, mobileOnly: false },
  { label: "Brands", href: null, desktopOnly: false, mobileOnly: false },
  {
    label: "Contact Us",
    href: ROUTE_PATHS.CONTACT,
    desktopOnly: false,
    mobileOnly: false,
  },
  // {
  //   label: "FAQ",
  //   href: ROUTE_PATHS.FAQ,
  //   desktopOnly: false,
  //   mobileOnly: false,
  // },
];

const DESKTOP_NAV_LINKS = NAV_LINKS.filter((l) => !l.mobileOnly).map(
  (l) => l.label,
);
const MOBILE_NAV_LINKS = NAV_LINKS.filter((l) => !l.desktopOnly).map(
  (l) => l.label,
);

const getNavHref = (label) =>
  NAV_LINKS.find((l) => l.label === label)?.href ??
  NAV_LINK_PATHS[label] ??
  null;


const getSearchType = (pathname) => {
  if (pathname.startsWith("/products") || pathname.startsWith("/brands") || pathname.startsWith("/category")) return "product";
  if (pathname.startsWith("/blog")) return "blog";
  return "";
};

const PakistanFlag = ({ className = "" }) => (
  <svg viewBox="0 0 30 20" className={className} aria-label="Pakistan" role="img">
    <rect width="30" height="20" fill="#01411C" />
    <rect width="7.5" height="20" fill="#fff" />
    <g fill="#fff" transform="translate(19.5,10)">
      <circle r="4.2" fill="#fff" />
      <circle r="4.2" cx="1.4" fill="#01411C" />
      <path
        d="M2.2 -3 l0.7 2.1 2.2 0 -1.8 1.3 0.7 2.1 -1.8 -1.3 -1.8 1.3 0.7 -2.1 -1.8 -1.3 2.2 0 z"
        transform="translate(2.6,-1)"
      />
    </g>
  </svg>
);

const ChevronDown = () => <ChevronDownIcon size={14} />;
const SearchIcon = () => <Search size={20} />;
const CloseIcon = () => <X size={22} />;
const MenuIcon = () => <Menu size={22} />;
const PlusIcon = () => <Plus size={16} />;
const MinusIcon = () => <Minus size={16} />;

export const Header = ({ products = [], blogs = [], brands = [], categories = [], contact = null }) => {
  const { pathname } = useLocation();

  const blogItems = useMemo(
    () => blogs.map((b) => ({ label: b.name, slug: b.slug, img: b.image })),
    [blogs],
  );
  const brandItems = useMemo(
    () => brands.map((b) => ({ label: b.name, slug: b.slug, img: b.image })),
    [brands],
  );

  const SIMPLE_MENU = useMemo(
    () => ({
      Blog: blogItems,
      Brands: brandItems,
    }),
    [blogItems, brandItems],
  );

  const categoryItems = useMemo(
    () => categories.map((c) => ({ label: c.name, slug: c.slug })),
    [categories],
  );

  const MOBILE_SUBMENUS = useMemo(() => {
    const prodItems = products.slice(0, 10).map((p) => ({
      label: p.name,
      slug: p.slug,
      img: p.images?.[0],
    }));
    return {
      Categories: categoryItems,
      Products: prodItems,
      Blog: blogItems,
      Brands: brandItems,
    };
  }, [products, categoryItems, blogItems, brandItems]);

  const isNavActive = (link) => {
    const pattern = getNavHref(link) ?? NAV_LINK_PATHS[link];
    if (!pattern) return false;
    // convert route pattern (/blog/:slug) to regex
    const regex = new RegExp(
      "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "(/|$)",
    );
    return regex.test(pathname);
  };

  const [activeMenu, setActiveMenu] = useState(null);
  const [displayedMenu, setDisplayedMenu] = useState(null);
  const fadeTimerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (activeMenu) {
      // New menu hovered — update content immediately, wrapper fades in
      setDisplayedMenu(activeMenu);
    }
    // When activeMenu is null, wrapper fades out — clear content after transition
    if (!activeMenu) {
      fadeTimerRef.current = setTimeout(() => setDisplayedMenu(null), 250);
    }
    return () => clearTimeout(fadeTimerRef.current);
  }, [activeMenu]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y <= 100) {
        setHeaderVisible(true);
      } else if (y > lastScrollY.current) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMobile = (link) => {
    if (mobileExpanded === link) {
      setMobileExpanded(null);
    } else {
      setMobileExpanded(link);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 w-full bg-white border-b border-gray-100 z-50 transition-transform duration-300`}
        onMouseLeave={() => {
          setActiveMenu(null);
        }}
      >
        {/* Top headline strip — Pakistan flag + phone */}
        {/* <div className="bg-blue-50 border-b border-blue-200 text-blue-700">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-8 h-8 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <PakistanFlag className="h-3.5 w-auto rounded-[1px] shadow-sm" />
            </div>
            {contact?.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="flex items-center gap-1.5 font-semibold text-blue-700 hover:text-blue-900 transition-colors"
              >
                <Phone size={13} />
                <span>{contact.phone}</span>
              </a>
            )}
          </div>
        </div> */}

        <div className="max-w-[1440px] py-2 mx-auto px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
          {/* Mobile: hamburger left */}
          <Text
            is="button"
            variant="btn-icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </Text>

          {/* Logo — center on mobile, left on desktop */}
          <Text is="a" variant="nav-logo" href="/" aria-label="Home">
            <div className="flex items-center h-16 w-48">
              <CompanyLogo className="h-[66px] w-auto object-contain" />
            </div>
          </Text>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {DESKTOP_NAV_LINKS.map((link) => {
              const linkHref = getNavHref(link);
              const isSimpleLink =
                !!linkHref && !SIMPLE_MENU[link] && link !== "Products" && link !== "Categories";
              const hasMenu =
                !isSimpleLink && (link === "Products" || link === "Categories" || SIMPLE_MENU[link]);
              const isActive = activeMenu === link;
              const isRouteActive = isNavActive(link);
              const activeVariant =
                isRouteActive || isActive ? "nav-link-active" : "nav-link";
              const activeLabelVariant =
                isRouteActive || isActive
                  ? "nav-link-label-active"
                  : "nav-link-label";

              if (isSimpleLink) {
                return (
                  <Text
                    key={link}
                    is="a"
                    href={linkHref}
                    variant={activeVariant}
                  >
                    <Text is="span" variant={activeLabelVariant}>
                      {link}
                    </Text>
                  </Text>
                );
              }

              const megaHref =
                link === "Products"
                  ? ROUTE_PATHS.PRODUCTS
                  : link === "Categories"
                    ? ROUTE_PATHS.CATEGORIES
                    : link === "Brands"
                      ? ROUTE_PATHS.BRANDS
                      : link === "Blog"
                        ? ROUTE_PATHS.BLOG
                        : null;

              return (
                <div
                  key={link}
                  className="relative"
                  onMouseEnter={() => hasMenu && setActiveMenu(link)}
                >
                  {megaHref ? (
                    <Text is="a" href={megaHref} variant={activeVariant}>
                      <Text is="span" variant={activeLabelVariant}>
                        {link}
                      </Text>
                      {hasMenu && <ChevronDown />}
                    </Text>
                  ) : (
                    <Text is="button" variant={activeVariant}>
                      <Text is="span" variant={activeLabelVariant}>
                        {link}
                      </Text>
                      {hasMenu && <ChevronDown />}
                    </Text>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right icons — search only */}
          <div className="flex items-center">
            <button
              className="text-gray-900 hover:text-blue-500 transition-colors p-1"
              onClick={() => setSearchOpen(true)}
              aria-label="Open search"
            >
              <SearchIcon />
            </button>
          </div>
        </div>

        {/* Desktop: single mega menu wrapper */}
        <div
          className={`hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-gray-100 shadow-lg z-50 transition-all duration-200 ease-in-out ${activeMenu && (activeMenu === "Products" || activeMenu === "Categories" || SIMPLE_MENU[activeMenu])
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 translate-y-2 pointer-events-none"
            }`}
        >
          {/* Products mega menu */}
          <ProductsMegaMenu visible={displayedMenu === "Products"} products={products} />

          {/* Categories mega menu */}
          <CategoriesMegaMenu visible={displayedMenu === "Categories"} categories={categories} />

          {/* Simple menu sections (Pages) */}
          {Object.entries(SIMPLE_MENU)
            .filter(([key]) => key !== "Blog" && key !== "Brands")
            .map(([key, items]) => (
              <div
                key={key}
                className={displayedMenu === key ? "block" : "hidden"}
              >
                <div className="max-w-[1440px] mx-auto px-8 py-8">
                  <ul className="space-y-3">
                    {items.map((item) => {
                      const label =
                        typeof item === "string" ? item : item.label;
                      const href =
                        typeof item === "string" ? "/" : `/blog/${item.slug}`;
                      return (
                        <li key={label}>
                          <Text
                            is="a"
                            href={href}
                            className="text-sm text-gray-600 hover:text-blue-500 transition-colors"
                          >
                            {label}
                          </Text>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ))}

          {/* Blog dropdown — links + hover image preview */}
          <NavDropdownWithImage
            visible={displayedMenu === "Blog"}
            items={blogItems}
            buildHref={(item) =>
              `${ROUTE_PATHS.BLOG_DETAIL.replace(":slug", item.slug)}`
            }
            seeAllHref={ROUTE_PATHS.BLOG}
          />
          <NavDropdownWithImage
            visible={displayedMenu === "Brands"}
            items={brandItems}
            buildHref={(item) => `${ROUTE_PATHS.BRANDS}/${item.slug}`}
            seeAllHref={ROUTE_PATHS.BRANDS}
          />
        </div>
      </header>

      {/* Mobile drawer overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] lg:hidden transition-opacity duration-300 cursor-none ${mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setMobileOpen(false)}
        onMouseMove={(e) => {
          setCursorPos({ x: e.clientX, y: e.clientY });
          setCursorVisible(true);
        }}
        onMouseLeave={() => setCursorVisible(false)}
      >
        <div
          style={{ left: cursorPos.x, top: cursorPos.y }}
          className={`fixed -translate-x-1/2 -translate-y-1/2 pointer-events-none w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md transition-opacity duration-150 ${cursorVisible ? "opacity-100" : "opacity-0"}`}
        >
          <CloseIcon />
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-[340px] max-w-[85vw] bg-white z-[70] lg:hidden flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <CompanyLogo className="h-8 w-auto" />
          <button
            onClick={() => setMobileOpen(false)}
            className="text-gray-700 hover:text-blue-500 transition-colors"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto">
          {MOBILE_NAV_LINKS.map((link) => {
            const linkHref = getNavHref(link);
            const isSimpleLink = !!linkHref && !MOBILE_SUBMENUS[link];
            const hasSub = !isSimpleLink && MOBILE_SUBMENUS[link];
            const isExpanded = mobileExpanded === link;
            const isRouteActive = isNavActive(link);

            return (
              <div key={link} className="border-b border-gray-100">
                {isSimpleLink ? (
                  <Text
                    is="a"
                    href={linkHref}
                    className={`w-full flex items-center px-5 py-4 text-sm font-medium transition-colors ${isRouteActive ? "text-blue-500" : "text-gray-900"}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link}
                  </Text>
                ) : (
                  <button
                    className={`w-full flex items-center justify-between px-5 py-4 text-sm font-medium transition-colors ${isRouteActive ? "text-blue-500" : "text-gray-900"}`}
                    onClick={() =>
                      hasSub ? toggleMobile(link) : setMobileOpen(false)
                    }
                  >
                    <span>{link}</span>
                    {hasSub && (isExpanded ? <MinusIcon /> : <PlusIcon />)}
                  </button>
                )}

                {/* Expanded submenu */}
                {hasSub && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${isExpanded ? "max-h-[600px]" : "max-h-0"}`}
                  >
                    <div className="pb-2">
                      {MOBILE_SUBMENUS[link]?.length > 0 ? (
                        MOBILE_SUBMENUS[link].map((item) => {
                          const label =
                            typeof item === "string" ? item : item.label;
                          const href =
                            typeof item === "string"
                              ? "/"
                              : !item.slug
                                ? "/"
                                : link === "Blog"
                                  ? `${ROUTE_PATHS.BLOG}/${item.slug}`
                                  : link === "Brands"
                                    ? `${ROUTE_PATHS.BRANDS}/${item.slug}`
                                    : link === "Categories"
                                      ? ROUTE_PATHS.CATEGORY_DETAIL.replace(":categorySlug", item.slug)
                                      : "/";
                          return (
                            <Text
                              key={label}
                              is="a"
                              href={href}
                              className="block pl-9 pr-5 py-2.5 text-sm text-gray-500 hover:text-blue-500 transition-colors border-t border-gray-50"
                            >
                              {label}
                            </Text>
                          );
                        })
                      ) : (
                        <p className="pl-9 pr-5 py-2.5 text-sm text-gray-400">
                          No items available
                        </p>
                      )}
                      {(link === "Blog" || link === "Brands" || link === "Categories") && (
                        <Text
                          is="a"
                          href={
                            link === "Blog"
                              ? ROUTE_PATHS.BLOG
                              : link === "Brands"
                                ? ROUTE_PATHS.BRANDS
                                : ROUTE_PATHS.CATEGORIES
                          }
                          className="block pl-9 pr-5 py-2.5 text-sm font-semibold text-blue-500 hover:text-blue-600 transition-colors border-t border-gray-100"
                        >
                          See all {link} →
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Drawer footer */}
        <div className="px-5 py-6 border-t border-gray-100 space-y-4">
          <button
            className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-sm py-2.5 text-sm text-gray-700 hover:border-gray-900 transition-colors"
            onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
          >
            <SearchIcon />
            <span>Search</span>
          </button>
          {contact && (
            <div className="text-xs text-gray-400 space-y-1">
              <p className="font-medium text-gray-600">Need help?</p>
              {contact.address && <p>Address: <span className="text-gray-600">{contact.address}</span></p>}
              {contact.email && <p>Email: <span className="text-gray-600">{contact.email}</span></p>}
              {contact.phone && <p>Phone: <span className="text-gray-600">{contact.phone}</span></p>}
            </div>
          )}
        </div>
      </div>
      {searchOpen && <SearchModal searchType={getSearchType(pathname)} onClose={() => setSearchOpen(false)} />}
    </>
  );
};
